import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  TextInput,
  Title,
  Text,
  Switch,
  Select,
  Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import {
  IconCalendar,
  IconCheck,
  IconRefresh,
  IconSearch,
  IconStatusChange,
  IconX,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { repositoryMdm, repositoryPos } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { changeSearchValue } from "../../../_base/helper/FunctionHelper";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const ComparePromotionPOSAndWEB = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    FromDate: _searchJson && checkedPinSearch ? _searchJson?.StartDate : "",
    ToDate: _searchJson && checkedPinSearch ? _searchJson?.EndDate : "",
    IsExist: _searchJson && checkedPinSearch ? _searchJson.IsExist : "true",
    Status: _searchJson && checkedPinSearch ? _searchJson.Status : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "pMaKm",
        header: "Mã CTKM trên POS",
        Cell: ({ row }) =>
          row.original.pMaKm ? (
            <Tooltip label="Xem chi tiết">
              <Badge
                variant="dot"
                color="green"
                onDoubleClick={() => {
                  navigate("/config-system/detail-promotion", {
                    state: { id: row.original.pIdKm },
                  });
                }}
                style={{ cursor: "pointer" }}
              >
                {row.original.pMaKm}
              </Badge>
            </Tooltip>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "pNgayBatDau",
        header: "Ngày bắt đầu CTKM trên POS",
        size: 275,
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "pNgayKetThuc",
        header: "Ngày kết thúc CTKM trên POS",
        size: 275,
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "pStatus",
        header: "Trạng thái duyệt CTKM trên POS",
        size: 275,
        Cell: ({ row }) =>
          row.original.pMaKm ? (
            <>
              <Badge
                color={getColorStatus(
                  row.original.pStatus,
                  row.original.pNgayBatDau,
                  row.original.pNgayKetThuc
                )}
              >
                {getStatusApproval(row.original.pStatus)}
              </Badge>
            </>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "pStatusChange",
        header: "Trạng thái CTKM trên POS",
        size: 275,
        Cell: ({ row }) =>
          row.original.pMaKm ? (
            <Badge
              color={getColorStatus(
                row.original.pStatus,
                row.original.pNgayBatDau,
                row.original.pNgayKetThuc
              )}
            >
              {getStatus(
                row.original.pStatus,
                row.original.pNgayBatDau,
                row.original.pNgayKetThuc
              )}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "wMaKm",
        header: "Mã CTKM trên WEB",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <Badge variant="dot" color="blue">
              {renderedCellValue}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "wNgayBatDau",
        header: "Ngày bắt đầu CTKM trên WEB",
        size: 275,
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "wNgayKetThuc",
        header: "Ngày kết thúc CTKM trên WEB",
        size: 275,
        Cell: ({ renderedCellValue }: any) => (
          <Text size="12.5px" fw={"500"}>
            {renderedCellValue && formatDateTransfer(renderedCellValue)}
          </Text>
        ),
      },
      {
        accessorKey: "wStatus",
        header: "Trạng thái duyệt CTKM trên WEB",
        size: 275,
        Cell: ({ row }) =>
          row.original.wMaKm ? (
            <>
              <Badge
                color={getColorStatus(
                  row.original.wStatus,
                  row.original.wNgayBatDau,
                  row.original.wNgayKetThuc
                )}
              >
                {getStatusApproval(row.original.wStatus)}
              </Badge>
            </>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "wStatusChange",
        header: "Trạng thái",
        size: 275,
        Cell: ({ row }) =>
          row.original.wMaKm ? (
            <Badge
              color={getColorStatus(
                row.original.wStatus,
                row.original.wNgayBatDau,
                row.original.wNgayKetThuc
              )}
            >
              {getStatus(
                row.original.wStatus,
                row.original.wNgayBatDau,
                row.original.wNgayKetThuc
              )}
            </Badge>
          ) : (
            <></>
          ),
      },
    ],
    []
  );

  function getColorStatus(
    status: string,
    effDate: Date,
    expDate: Date
  ): string {
    const now = new Date();
    const effectiveDate = effDate ? new Date(effDate) : null;
    const expirationDate = expDate ? new Date(expDate) : null;
    switch (status) {
      case "2": // Đã duyệt
        if (effectiveDate && expirationDate) {
          if (now >= effectiveDate && now <= expirationDate) {
            return "teal"; // Đang chạy
          } else if (now > expirationDate) {
            return "gray"; // Hết hiệu lực
          } else if (now < effectiveDate) {
            return "teal.4"; // Hết hiệu lực
          }
          return "teal";
        }
        return "blue"; // Đang xét duyệt
      case "3": // Dừng duyệt
        return "yellow"; // Đang xét duyệt (có thể chỉnh sửa, không cho xóa)
      case "4": // Từ chối
        return "red"; // Không áp dụng (có thể sửa, không cho xóa)
      case "5": // Hủy duyệt
        return "blue"; // Chưa áp dụng (có thể sửa, không cho xóa)
      default: // Chưa duyệt
        return "blue"; // Đang xét duyệt (có thể chỉnh sửa, có thể xóa)
    }
  }

  function getStatusApproval(text: any) {
    switch (text) {
      case "2":
        return "Đã duyệt";
      case "3":
        return "Tạm dừng";
      case "4":
        return "Từ chối";
      case "5":
        return "Hủy duyệt";
      default:
        return "Chưa duyệt";
    }
  }

  function getStatus(
    text: any,
    effDate?: string | Date,
    expDate?: string | Date
  ): string {
    const now = new Date();
    const effectiveDate = effDate ? new Date(effDate) : null;
    const expirationDate = expDate ? new Date(expDate) : null;

    switch (text) {
      case "2": // Đã duyệt
        if (effectiveDate && expirationDate) {
          if (now >= effectiveDate && now <= expirationDate) {
            return "Đang chạy";
          } else if (now > expirationDate) {
            return "Hết hiệu lực";
          } else if (now < effectiveDate) {
            return "Chưa đến thời gian chạy";
          }
        }
        return "Đang xét duyệt";
      case "3": // Dừng duyệt
        return "Đang xét duyệt";
      case "4": // Từ chối
        return "Không áp dụng ";
      case "5": // Hủy duyệt
        return "Chưa áp dụng";
      default: // Chưa duyệt
        return "Chưa duyệt";
    }
  }

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/ComparePosWeb/so-sanh-ctkm?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const res = await repositoryMdm.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        })
      );
    }
  };

  // const exportDataExcel = async () => {
  //   open();
  //   try {
  //     let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

  //     const params = new URLSearchParams();
  //     Object.entries(search).forEach(([key, value]) => {
  //       if (Array.isArray(value) && value.length > 0) {
  //         value.forEach((item) => params.append(key, String(item)));
  //       } else if (typeof value === "string" && value.length > 0) {
  //         params.append(key, value);
  //       }
  //     });

  //     if (keySearch.length > 0) {
  //       url += `&KeySearch=${keySearch?.trim()}`;
  //     }

  //     url += `&${params.toString()}`;

  //     const baseUrl = `/api/v1/ItemComparison/export-excel${url}`;

  //     const _url = BASE_API_MDM + baseUrl;
  //     const token = localStorage.getItem("token");

  //     const response = await fetch(_url, {
  //       method: "GET",
  //       headers: {
  //         "Content-Type": "application/vnd.ms-excel",
  //         Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       NotificationExtension.Fails("Export Excel thất bại !");
  //     } else {
  //       const blob = await response.blob();
  //       const urlss = window.URL.createObjectURL(blob);
  //       const _name = new Date().toISOString().slice(0, 10);
  //       let filename = "danh_sach_voucher.xlsx";
  //       const link = document.createElement("a");
  //       link.href = urlss;
  //       link.download = filename;
  //       document.body.appendChild(link);
  //       link.click();
  //       link.remove();
  //       window.URL.revokeObjectURL(url);
  //       NotificationExtension.Success("Export Excel thành công !");
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   } finally {
  //     close();
  //   }
  // };

  const handleSyncPromotion = async (ids: string[]) => {
    open();
    try {
      const response = await repositoryPos.post<MessageResponse<any>>(
        "/api/v1/TblPromotion/Sync-flash",
        ids
      );
      if (response && response.success) {
        NotificationExtension.Success("Đồng bộ thành công !");
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        NotificationExtension.Fails("Đồng bộ thất bại !");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningListVourcher"
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: ["mrt-row-select", "mrt-row-numbers", "vourcherCode"],
          right: ["enforcement", "action"],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage()
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningListVourcher",
      JSON.stringify(columnPinning)
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (185 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            {/* <Button
              leftSection={<IconRefresh size={14} />}
              color="cyan"
              onClick={() => {
                const selectIdCTKM = table
                  .getSelectedRowModel()
                  .rows.map((row) => row.original.pIdKm);
                if (selectIdCTKM.length > 0) {
                  handleSyncPromotion(selectIdCTKM);
                } else {
                  NotificationExtension.Fails("Chọn dữ liệu để thao tác !");
                }
              }}
            >
              Đồng bộ CTKM
            </Button> */}
            {/* <Button
                leftSection={<IconFileTypeCsv size={16} />}
                onClick={() => exportDataExcel()}
              >
                Export Excel
              </Button> */}
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Từ ngày (Lọc theo ngày kết thúc CTKM)"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              value={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate ?? "")
                  : null
              }
              maxDate={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate)
                  : new Date()
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000
                      )?.toISOString()
                    : "",
                  "FromDate",
                  setSearch
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Đến ngày (Lọc theo ngày kết thúc CTKM)"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              clearable
              value={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate ?? "")
                  : null
              }
              minDate={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate)
                  : undefined
              }
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000
                      )?.toISOString()
                    : "",
                  "ToDate",
                  setSearch
                );
              }}
              leftSection={<IconCalendar color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Select
              placeholder="Trạng thái duyệt"
              data={[
                { label: "Chưa duyệt", value: "1" },
                { label: "Đã duyệt", value: "2" },
                { label: "Tạm dừng", value: "3" },
                { label: "Từ chối", value: "4" },
                { label: "Hủy duyệt", value: "5" },
              ]}
              leftSection={<IconStatusChange color="#15aabf" />}
              value={search.Status}
              onChange={(e) =>
                changeSearchValue(e ? e : "", "Status", setSearch)
              }
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 4 }}>
            <Flex gap={"xs"} align={"center"}>
              <Switch
                onLabel={"Không tồn tại trên WEB"}
                offLabel={"Tồn tại trên WEB"}
                checked={search.IsExist === "true"}
                color="red"
                size="lg"
                onClick={() =>
                  changeSearchValue(
                    search.IsExist === "true" ? "false" : "true",
                    "IsExist",
                    setSearch
                  )
                }
                thumbIcon={
                  search.IsExist !== "true" ? (
                    <IconCheck
                      size={12}
                      color="var(--mantine-color-teal-6)"
                      stroke={3}
                    />
                  ) : (
                    <IconX
                      size={12}
                      color="var(--mantine-color-red-6)"
                      stroke={3}
                    />
                  )
                }
              />
              <Button
                onClick={() => fetchData()}
                leftSection={<IconSearch size={14} />}
              >
                Tìm kiếm
              </Button>
            </Flex>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ComparePromotionPOSAndWEB;
